import React, { useState, useEffect, useCallback } from 'react';
import {
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend
} from 'recharts';
import { FiDollarSign, FiAlertCircle } from 'react-icons/fi';
import { fetchProjects, fetchRuns } from '../services/api';

const CostAnalytics = () => {
  const [costData, setCostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  const loadCostData = useCallback(async () => {
    try {
      const projectsResponse = await fetchProjects();
      const runsResponse = await fetchRuns();

      // Handle both cases: when fetchProjects/fetchRuns return arrays directly or objects with properties
      const projects = Array.isArray(projectsResponse) ? projectsResponse : (projectsResponse?.projects || []);
      const runs = Array.isArray(runsResponse) ? runsResponse : (runsResponse?.runs || []);

      // When no projects exist (e.g. all deleted), show zeros so values recalculate after delete
      if (!projects || projects.length === 0) {
        setCostData({
          totalCost: 0,
          costByModel: [],
          costTrends: [],
          costPerToken: '0.000000',
          expensiveQueries: [],
          totalTokens: 0
        });
        setLoading(false);
        return;
      }

      // Project Details logic: use project-level cost (project.total_cost or project.costs) as source of truth
      // So Cost Analytics total = sum of same values shown in Project Details and Project Distribution
      let totalCost = 0;

      const projectTotalCosts = projects.reduce((sum, project) => sum + (parseFloat(project.total_cost) || 0), 0);
      const projectCostsSum = projects.reduce((sum, project) => {
        const c = project.costs && typeof project.costs === 'object'
          ? Object.values(project.costs).reduce((a, b) => a + (parseFloat(b) || 0), 0)
          : 0;
        return sum + c;
      }, 0);

      if (projectTotalCosts > 0) {
        totalCost = projectTotalCosts;
      } else if (projectCostsSum > 0) {
        totalCost = projectCostsSum;
      } else {
        totalCost = runs.reduce((sum, run) => sum + getRunCost(run), 0);
      }

      function getRunCost(run) {
        if (run.cost != null && !isNaN(parseFloat(run.cost)) && parseFloat(run.cost) > 0) return parseFloat(run.cost);
        if (run.costs && typeof run.costs === 'object') {
          const s = Object.values(run.costs).reduce((a, c) => a + (parseFloat(c) || 0), 0);
          if (s > 0) return s;
        }
        const llmCost = run.llm_calls?.reduce((a, c) => a + (parseFloat(c.cost) || 0), 0) || 0;
        const ocrCost = run.ocr_metrics?.reduce((a, o) => a + (parseFloat(o.cost) || 0), 0) || 0;
        const embeddingCost = run.embedding_metrics?.reduce((a, e) => a + (parseFloat(e.cost) || 0), 0) || 0;
        return llmCost + ocrCost + embeddingCost;
      }

      // Cost by model: use same source as Total Cost (project-level costs) so the pie always matches the total
      const costByModel = {};
      projects.forEach(project => {
        const costsDict = project.costs && typeof project.costs === 'object' && Object.keys(project.costs).length > 0
          ? project.costs
          : (project.costs_by_model && typeof project.costs_by_model === 'object' ? project.costs_by_model : null);
        if (costsDict) {
          Object.entries(costsDict).forEach(([model, cost]) => {
            if (model && model.toLowerCase() !== 'unknown') {
              const c = parseFloat(cost) || 0;
              if (c > 0) costByModel[model] = (costByModel[model] || 0) + c;
            }
          });
        }
      });
      const sumByModel = Object.values(costByModel).reduce((a, b) => a + b, 0);
      if (totalCost > sumByModel && totalCost - sumByModel > 1e-10) {
        costByModel['Unspecified'] = (costByModel['Unspecified'] || 0) + (totalCost - sumByModel);
      }

      // Cost trends over time
      const costTrends = calculateCostTrends(runs, timeRange);

      // ✅ Total tokens: prefer project-level token_usage (same source as Project Details), then fallback to runs
      const projectTotalTokens = projects.reduce((sum, project) => {
        const t = project.token_usage ?? project.total_tokens ?? project.tokens_used ?? 0;
        return sum + (Number(t) || 0);
      }, 0);

      let totalTokens = projectTotalTokens;
      if (totalTokens <= 0) {
        // Fallback: aggregate from runs. Priority: llm_calls.total_tokens > run.token_usage > run.total_tokens
        totalTokens = runs.reduce((sum, run) => {
          const runTokensFromCalls = run.llm_calls?.reduce((callSum, call) => {
            return callSum + (call.total_tokens || call.input_tokens + call.output_tokens || 0);
          }, 0) || 0;
          const runTokenUsage = run.token_usage || 0;
          const runTotalTokens = run.total_tokens || 0;
          const runTokens = runTokensFromCalls > 0 ? runTokensFromCalls : (runTokenUsage > 0 ? runTokenUsage : runTotalTokens);
          return sum + runTokens;
        }, 0);
      }

      // Cost per 1K tokens: use more decimals so small values (e.g. $0.000034) don't show as $0.0000
      const costPer1K = totalTokens > 0 ? (totalCost / totalTokens) * 1000 : 0;
      const costPerToken = costPer1K > 0 ? (costPer1K < 0.0001 ? costPer1K.toFixed(8) : costPer1K.toFixed(6)) : '0.000000';

      // ✅ Top expensive queries - aggregate by unique prompt
      // Calculate cost from all llm_calls in each run, not just the first one
      const queryMap = new Map();

      runs.forEach(run => {
        if (run.llm_calls && run.llm_calls.length > 0) {
          run.llm_calls.forEach(call => {
            if (call.cost && call.cost > 0) {
              const prompt = call.prompt || call.input || 'N/A';
              const model = call.model || 'unknown';
              // Only include valid models
              if (model && model.toLowerCase() !== 'unknown' && model.trim() !== '') {
                const key = `${prompt}:::${model}`; // Unique key for prompt+model combination

                if (queryMap.has(key)) {
                  const existing = queryMap.get(key);
                  existing.cost += call.cost || 0;
                  existing.tokens += (call.total_tokens || call.input_tokens + call.output_tokens || 0);
                  existing.count += 1;
                } else {
                  queryMap.set(key, {
                    prompt: prompt.length > 100 ? prompt.substring(0, 100) + '...' : prompt,
                    cost: call.cost || 0,
                    tokens: call.total_tokens || call.input_tokens + call.output_tokens || 0,
                    model,
                    count: 1
                  });
                }
              }
            }
          });
        }
      });

      // Convert to array and sort by total cost
      const expensiveQueries = Array.from(queryMap.values())
        .sort((a, b) => b.cost - a.cost)
        .slice(0, 5)
        .map(query => ({
          ...query,
          avgCost: query.cost / query.count
        }));

      setCostData({
        totalCost,
        costByModel: Object.entries(costByModel).map(([name, value]) => ({ name, value })),
        costTrends,
        costPerToken,
        expensiveQueries,
        totalTokens
      });
    } catch (error) {
      console.error('Error loading cost data:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadCostData();
  }, [timeRange, loadCostData]);

  const calculateCostTrends = (runs, range) => {
    const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
    const trends = [];
    // ✅ Use Local Time (IST) for date calculations to match backend IST timestamps
    const now = new Date();

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      // Get date string in local timezone (IST) for display and comparison
      // Using manual formatting to ensure local time is preserved (toISOString converts to UTC)
      const dateStr = date.getFullYear() + '-' + String(date.getMonth() + 1).padStart(2, '0') + '-' + String(date.getDate()).padStart(2, '0');

      // ✅ Filter runs by matching date in Local/IST timestamps
      // Handle both UTC timestamps with 'Z' suffix and ISO format
      const runsForDay = runs.filter(run => {
        if (!run.start_time) return false;

        try {
          // Parse the timestamp
          const runDate = new Date(run.start_time);
          if (isNaN(runDate.getTime())) return false;

          // Convert to local date string for comparison
          const runDateStr = runDate.getFullYear() + '-' + String(runDate.getMonth() + 1).padStart(2, '0') + '-' + String(runDate.getDate()).padStart(2, '0');
          return runDateStr === dateStr;
        } catch (error) {
          // Fallback: check if timestamp string contains the date
          return run.start_time.includes(dateStr);
        }
      });

      const dayCost = runsForDay.reduce((sum, run) => {
        // Single source: run.cost (consolidated) or run.costs sum, then llm_calls+ocr_metrics (no double-count)
        let runCost = 0;
        if (run.cost != null && !isNaN(parseFloat(run.cost)) && parseFloat(run.cost) > 0) runCost = parseFloat(run.cost);
        else if (run.costs && typeof run.costs === 'object') {
          runCost = Object.values(run.costs).reduce((a, c) => a + (parseFloat(c) || 0), 0);
        }
        if (runCost === 0) {
          runCost = (run.llm_calls?.reduce((a, c) => a + (parseFloat(c.cost) || 0), 0) || 0)
            + (run.ocr_metrics?.reduce((a, o) => a + (parseFloat(o.cost) || 0), 0) || 0)
            + (run.embedding_metrics?.reduce((a, e) => a + (parseFloat(e.cost) || 0), 0) || 0);
        }
        return sum + runCost;
      }, 0);

      trends.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        cost: dayCost
      });
    }

    return trends;
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Don't render if no cost data
  if (!loading && (!costData || costData.totalCost === 0)) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
            <FiDollarSign className="w-8 h-8 text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Cost Data Available</h3>
          <p className="text-gray-500">Cost analytics will appear here once cost data is available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <FiDollarSign className="w-8 h-8 text-green-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Cost Analytics</h2>
              <p className="text-gray-600">Monitor your API usage costs</p>
            </div>
          </div>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Total Cost</p>
            <p className="text-2xl font-bold text-gray-900">
              ${costData?.totalCost ? costData.totalCost.toFixed(6) : '0.000000'}
              {(!costData?.totalCost || costData.totalCost === 0) && (
                <span className="text-xs text-yellow-600 block mt-1">
                  Check console for debugging info
                </span>
              )}
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Cost per 1K Tokens</p>
            <p className="text-2xl font-bold text-gray-900">${costData?.costPerToken ?? '0.000000'}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Total Tokens</p>
            <p className="text-2xl font-bold text-gray-900">
              {costData?.totalTokens?.toLocaleString() || '0'}
            </p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Active Models</p>
            <p className="text-2xl font-bold text-gray-900">
              {costData?.costByModel?.length || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cost by Model Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Cost Distribution by Model</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={costData?.costByModel || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {costData?.costByModel?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `$${value.toFixed(4)}`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Cost Trends Line Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Cost Trends</h3>
          {costData?.costTrends && costData.costTrends.some(trend => trend.cost > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={costData.costTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis
                  tickFormatter={(value) => value > 0 ? `$${value.toFixed(6)}` : '$0'}
                  domain={[0, 'dataMax + 0.000001']}
                />
                <Tooltip formatter={(value) => `$${value.toFixed(8)}`} />
                <Line
                  type="monotone"
                  dataKey="cost"
                  stroke="#8884d8"
                  strokeWidth={2}
                  dot={{ fill: '#8884d8' }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center">
              <div className="text-center">
                <p className="text-gray-500 mb-2">No cost data available for the selected time range</p>
                <p className="text-sm text-gray-400">Cost trends will appear when runs have associated costs</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Top Expensive Queries */}
      <div className="bg-white p-6 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <FiAlertCircle className="w-5 h-5 mr-2 text-yellow-600" />
          Top Expensive Queries
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Query
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Model
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Tokens
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Cost
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {costData?.expensiveQueries?.map((query, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="truncate max-w-xs" title={query.prompt}>
                      {query.prompt}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {query.model}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {query.count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {query.tokens.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    ${query.cost.toFixed(4)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CostAnalytics; 